import { Fragment, useState, useMemo, useCallback, useEffect } from 'react';
import intl from 'react-intl-universal';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';

// functions
import useApi from 'shared/hooks/api';
import localData from 'utils/localData';
import { updateArrayItemById } from 'utils/javascript';
import { getFirstDayPreviousMonth } from 'utils/dateTime';

// constants
import { DOLPHIN_API } from 'shared/constants/apis';
import { USER_DATA } from 'shared/constants/users';

// components and parts
import { PageLoader, PageError } from 'components';
import { getSentTableColumns } from './tableColumns.jsx';
import { IssueStatusForClient } from 'shared/constants/issues';
import History from 'views/History';
import logger from 'utils/logger';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
const log = logger.createLogger('SentHistoryContent');

const REFRESH_TIME = 1000;
const propTypes = {
  type: PropTypes.string,
};

const SentHistoryContent = ({ type }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const currentDate = new Date();
  currentDate.setHours(23, 59, 59, 999);
  const [timeRangeStart, setTimeRangeStart] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.HISTORY_START_DATE);
    return saved ? new Date(saved) : getFirstDayPreviousMonth(currentDate);
  });
  const [timeRangeEnd, setTimeRangeEnd] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.HISTORY_END_DATE);
    return saved ? new Date(saved) : currentDate;
  });
  const [filteredTableData, setFilteredTableData] = useState([]);

  useEffect(() => {
    if (location.state?.shouldRefresh) {
      fetchData();
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.HISTORY_START_DATE,
      timeRangeStart.toISOString()
    );
  }, [timeRangeStart]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.HISTORY_END_DATE,
      timeRangeEnd.toISOString()
    );
  }, [timeRangeEnd]);

  const handleOpenIssueDetails = useCallback(
    (issueId) => {
      navigate(`issues/${issueId}`, {
        state: {
          background: currentPath,
        },
      });
    },
    [navigate, currentPath]
  );

  const columns = useMemo(
    () => getSentTableColumns(handleOpenIssueDetails),
    [handleOpenIssueDetails]
  );

  // TODO: [Privilege] different api for doctor or staff usage
  const [{ data, error, setLocalData }, fetchData] = useApi.useGet(
    DOLPHIN_API.ISSUES_SENT,
    {
      params: {
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        type: type,
        timeRangeStart: timeRangeStart,
        timeRangeEnd: timeRangeEnd,
      },
    }
  );

  if (!data) return <PageLoader />;
  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );

  const { sentIssues, doctors } = data;
  sentIssues.sort((issue1, issue2) => issue2.id - issue1.id);
  const tableData = sentIssues.map((issue) => {
    return {
      case_title: issue.title,
      patientName: issue.patientName,
      status: IssueStatusForClient[issue.status],
      state: issue.state,
      order_time: issue.createdAt,
      deadline: issue.deadline,
      to_hospital: issue.dstOrgName,
      returned_time: issue.returnedAt,
      view: issue.id,
    };
  });

  const updateLocalProjectIssues = (issueId, updatedFields) => {
    setLocalData((currentData) => ({
      sentIssues: updateArrayItemById(
        currentData.sentIssues,
        issueId,
        updatedFields
      ),
      doctors: doctors,
    }));
  };

  const handleRowModelChange = (rowModel) => {
    const filteredData = rowModel.map((row) => row.original);
    const formattedData = filteredData.map((issue) => {
      return {
        id: issue.view,
        title: issue.case_title,
        patientName: issue.patientName,
        state: issue.state,
        status: issue.status,
        to_hospital: issue.to_hospital,
        order_time: issue.order_time,
        returned_time: issue.returned_time,
      };
    });
    setFilteredTableData(formattedData);
  };

  return (
    <Fragment>
      <History
        tableTitle={intl.get('case_list_text_sent_table_title')}
        columns={columns}
        tableData={tableData}
        handleOpenIssueDetails={handleOpenIssueDetails}
        updateLocalProjectIssues={(issueId, updatedFields) => {
          updateLocalProjectIssues(issueId, updatedFields);
          setTimeout(() => {
            fetchData();
          }, REFRESH_TIME);
        }}
        reloadData={fetchData}
        setTimeRangeEnd={setTimeRangeEnd}
        setTimeRangeStart={setTimeRangeStart}
        timeRangeStart={timeRangeStart}
        timeRangeEnd={timeRangeEnd}
        issues={sentIssues}
        doctors={doctors}
        isSender={true}
        onRowModelChange={handleRowModelChange}
      />
      <Outlet
        context={{
          doctors,
          updateLocalProjectIssues,
          issues: filteredTableData,
        }}
      />
    </Fragment>
  );
};

SentHistoryContent.propTypes = propTypes;

export default SentHistoryContent;
